/**
 * Arena definitions as data (02-arena §2) plus the few pure helpers the sim
 * needs: the team transform (the ONLY place mirroring exists — 02 §1), the
 * pad/bounds clamp (02 §5), and spawn selection (02 §6 / 10 §1).
 */
import * as C from './constants.js'
import type { Arena, Bounds, Pad, Pillar, PlayerState, Prop, Team, Vec2, Vec3, Zone } from './types.js'

function corridorA(): Arena {
  const hw = C.ARENA_WIDTH / 2
  // GAP_START/GAP_END are the zone edges; ZONE_DEPTH is their depth (01 §B keeps all three).
  const zones: Zone[] = [
    { team: 0, xMin: -hw, xMax: hw, zMin: 0, zMax: Math.min(C.GAP_START, C.ZONE_DEPTH) },
    { team: 1, xMin: -hw, xMax: hw, zMin: Math.max(C.GAP_END, C.ARENA_LENGTH - C.ZONE_DEPTH), zMax: C.ARENA_LENGTH },
  ]
  const pads: Pad[] = [
    { id: 'A', shape: 'rect', x: 0, z: C.GAP_START / 2, w: hw, d: C.GAP_START / 2, r: 0, y: C.FLOOR_Y, team: 0, behaviour: 'static', params: {} },
    { id: 'B', shape: 'rect', x: 0, z: (C.GAP_END + C.ARENA_LENGTH) / 2, w: hw, d: (C.ARENA_LENGTH - C.GAP_END) / 2, r: 0, y: C.FLOOR_Y, team: 1, behaviour: 'static', params: {} },
  ]
  const L = C.ARENA_LENGTH
  const walls = [
    { ax: -hw, az: 0, bx: hw, bz: 0, yMin: 0, yMax: C.WALL_HEIGHT },
    { ax: -hw, az: L, bx: hw, bz: L, yMin: 0, yMax: C.WALL_HEIGHT },
    { ax: -hw, az: 0, bx: -hw, bz: L, yMin: 0, yMax: C.WALL_HEIGHT },
    { ax: hw, az: 0, bx: hw, bz: L, yMin: 0, yMax: C.WALL_HEIGHT },
  ]
  // PILLAR_COUNT pillars spread symmetrically at ±PILLAR_X (2 → one each side).
  const pillars = Array.from({ length: C.PILLAR_COUNT }, (_, i) => {
    const n: number = C.PILLAR_COUNT
    const t = n === 1 ? 0 : (i / (n - 1)) * 2 - 1
    return { x: t * C.PILLAR_X, z: C.PILLAR_Z, r: C.PILLAR_RADIUS, h: C.PILLAR_HEIGHT }
  })
  // Team-local spawn tables; team B goes through toWorld.
  const pair: Vec2[] = [{ x: -C.SPAWN_X_PAIR, z: C.SPAWN_BACK_OFFSET }, { x: C.SPAWN_X_PAIR, z: C.SPAWN_BACK_OFFSET }]
  const hub: Vec2[] = C.HUB_SPAWN_X.map((x) => ({ x, z: C.SPAWN_BACK_OFFSET }))
  return {
    id: 'corridor_a', length: L, width: C.ARENA_WIDTH, floorY: C.FLOOR_Y,
    zones, pads, walls, pillars,
    spawns: { 0: pair, 1: pair.map((p) => toWorld(p, 1, L)) },
    hubSpawns: { 0: hub, 1: hub.map((p) => toWorld(p, 1, L)) },
    ceiling: null,
    props: [], camera: 'fixed', hub: { filler: true, moveMul: 1 },
    // Render-only (spec §3): the DEFAULT_CAMERA numbers (01 §C) and the two floodlight pairs.
    cam: { back: C.CAM_BACK, height: C.CAM_HEIGHT, pitchDeg: C.CAM_PITCH_DEG, hfovDeg: C.CAM_HFOV_DEG, near: C.CAM_NEAR },
    lights: [{ x: -4, y: 6, z: 7.5 }, { x: 4, y: 6, z: 7.5 }, { x: -4, y: 6, z: 22.5 }, { x: 4, y: 6, z: 22.5 }],
  }
}

/** A fixed-camera battle arena from its own literals (Sprint 10 spec §3; no 01 §B constant moves). Zones are the
 *  front third of each side, spawns sit L/10 in from the back wall at ±W/4.8, hub slots scale `HUB_SPAWN_X` by W/12,
 *  four floodlights at (±W/3, 8, L/4) and (±W/3, 8, 3L/4). Camera and heights are drawing data. */
export interface BattleArenaSpec { id: string; L: number; W: number; wallH: number; pillars: Pillar[]; cam: NonNullable<Arena['cam']>; voidLook?: Arena['voidLook']; moveMul?: number; discMul?: number }
export function battleArena(o: BattleArenaSpec): Arena {
  const { L, W, wallH } = o
  const hw = W / 2
  const zoneDepth = L / 3
  const gapStart = zoneDepth, gapEnd = L - zoneDepth
  const zones: Zone[] = [
    { team: 0, xMin: -hw, xMax: hw, zMin: 0, zMax: gapStart },
    { team: 1, xMin: -hw, xMax: hw, zMin: gapEnd, zMax: L },
  ]
  const pads: Pad[] = [
    { id: 'A', shape: 'rect', x: 0, z: gapStart / 2, w: hw, d: gapStart / 2, r: 0, y: C.FLOOR_Y, team: 0, behaviour: 'static', params: {} },
    { id: 'B', shape: 'rect', x: 0, z: (gapEnd + L) / 2, w: hw, d: (L - gapEnd) / 2, r: 0, y: C.FLOOR_Y, team: 1, behaviour: 'static', params: {} },
  ]
  const walls = [
    { ax: -hw, az: 0, bx: hw, bz: 0, yMin: 0, yMax: wallH },
    { ax: -hw, az: L, bx: hw, bz: L, yMin: 0, yMax: wallH },
    { ax: -hw, az: 0, bx: -hw, bz: L, yMin: 0, yMax: wallH },
    { ax: hw, az: 0, bx: hw, bz: L, yMin: 0, yMax: wallH },
  ]
  const back = L / 10, pairX = W / 4.8
  const pair: Vec2[] = [{ x: -pairX, z: back }, { x: pairX, z: back }]
  const hub: Vec2[] = C.HUB_SPAWN_X.map((x) => ({ x: x * W / 12, z: back }))
  const lx = W / 3
  const lights: Vec3[] = [{ x: -lx, y: 8, z: L / 4 }, { x: lx, y: 8, z: L / 4 }, { x: -lx, y: 8, z: (3 * L) / 4 }, { x: lx, y: 8, z: (3 * L) / 4 }]
  return {
    id: o.id, length: L, width: W, floorY: C.FLOOR_Y,
    zones, pads, walls, pillars: o.pillars.map((p) => ({ ...p })),
    spawns: { 0: pair, 1: pair.map((p) => toWorld(p, 1, L)) },
    hubSpawns: { 0: hub, 1: hub.map((p) => toWorld(p, 1, L)) },
    ceiling: null,
    props: [], camera: 'fixed', hub: { filler: true, moveMul: 1 },
    cam: { ...o.cam },
    lights,
    ...(o.voidLook ? { voidLook: o.voidLook } : {}),
    ...(o.moveMul && o.moveMul !== 1 ? { moveMul: o.moveMul } : {}),
    ...(o.discMul && o.discMul !== 1 ? { discMul: o.discMul } : {}),
  }
}

/** `corridor_xl`: the corridor at 4/3 scale in plan (40 × 16 m), same wall/pillar height. Opt-in via
 *  HUB_ARENA=corridor_xl (D15: default pending playtest). Scaled framing: back/height × 4/3 at the same lens. */
const corridorXL = (): Arena => battleArena({
  id: 'corridor_xl', L: 40, W: 16, wallH: 3,
  pillars: [{ x: -3.2, z: 20, r: 0.7, h: 3 }, { x: 3.2, z: 20, r: 0.7, h: 3 }],
  cam: { back: 28 / 3, height: 8, pitchDeg: 16, hfovDeg: 62, near: 0.5 },
})

/** Wide "field" arenas for the grand-scale iteration (user reads 2026-09-14: "3–4× wider, camera further back, no
 *  pillars in the middle", then "get rid of the screen in the middle, narrow it 10 %, lift the camera and angle down").
 *  9 m walls, no pillars, the gap drawn as plain floor (still uncrossable), camera 14 m back / 11 m up / 20° down.
 *  Opt-in via HUB_ARENA=field_40x36 / field_30x43; `?cam=back,height,pitch,fov` overrides the lens on the client. */
const field40x36 = (): Arena => battleArena({ id: 'field_40x36', moveMul: 1.35, discMul: 1.5, L: 40, W: 36, wallH: 9, pillars: [], voidLook: 'floor', cam: { back: 14, height: 11, pitchDeg: 20, hfovDeg: 75, near: 0.5 } })
const field60x26 = (): Arena => battleArena({ id: 'field_60x26', moveMul: 1.35, discMul: 1.5, L: 60, W: 26, wallH: 9, pillars: [], voidLook: 'floor', cam: { back: 14, height: 11, pitchDeg: 20, hfovDeg: 68, near: 0.5 } })
const field30x43 = (): Arena => battleArena({ id: 'field_30x43', moveMul: 1.35, discMul: 1.5, L: 30, W: 43, wallH: 9, pillars: [], voidLook: 'floor', cam: { back: 14, height: 11, pitchDeg: 20, hfovDeg: 80, near: 0.5 } })

/** Deterministic 32-bit hash → [0, 1). Pure; the sim has no Math.random. */
function hash01(seed: number, i: number, j: number): number {
  let h = (seed ^ Math.imul(i + 0x9e37, 0x85ebca6b) ^ Math.imul(j + 0x79b9, 0xc2b2ae35)) >>> 0
  h ^= h >>> 16; h = Math.imul(h, 0x7feb352d) >>> 0
  h ^= h >>> 15; h = Math.imul(h, 0x846ca68b) >>> 0
  h ^= h >>> 16
  return (h >>> 0) / 4294967296
}

const LANDMARK_SHAPES: readonly { r: number; h: number }[] = [{ r: 0.7, h: 3 }, { r: 1.1, h: 4.5 }, { r: 0.5, h: 2.2 }, { r: 1.6, h: 6 }]

/** Seeded landmark pillars on a jittered lattice; cells inside `clear` of a terminal are skipped (spec §1). */
export function landmarkPillars(size: number, cell: number, clear: number, terminals: readonly Vec2[], seed = 7): Pillar[] {
  const out: Pillar[] = []
  const hw = size / 2
  const n = Math.floor(size / cell)
  for (let i = 0; i < n; i++) for (let j = 0; j < n; j++) {
    if (hash01(seed, i, j) < 0.12) continue // sparse
    const x = -hw + (i + 0.5) * cell + (hash01(seed + 1, i, j) - 0.5) * cell * 0.7
    const z = (j + 0.5) * cell + (hash01(seed + 2, i, j) - 0.5) * cell * 0.7
    if (terminals.some((t) => Math.hypot(x - t.x, z - t.z) < clear)) continue
    const shape = LANDMARK_SHAPES[Math.floor(hash01(seed + 3, i, j) * LANDMARK_SHAPES.length)]
    out.push({ x, z, r: shape.r, h: shape.h })
  }
  return out
}

function openworldA(): Arena {
  const S = C.OPENWORLD_SIZE, hw = S / 2
  const extent = { xMin: -hw, xMax: hw, zMin: 0, zMax: S }
  const zones: Zone[] = [{ team: 0, ...extent }, { team: 1, ...extent }]
  const pads: Pad[] = [{ id: 'deck', shape: 'rect', x: 0, z: S / 2, w: hw, d: S / 2, r: 0, y: C.FLOOR_Y, team: null, behaviour: 'static', params: {} }]
  const terminal: Prop = { kind: 'terminal', x: 0, z: S / 2, yaw: 0 }
  const pillars = landmarkPillars(S, C.LANDMARK_CELL, C.LANDMARK_CLEAR_RADIUS, [terminal])
  // Spawn ring (spec §1): 8 slots at 6 m around the terminal, shared by both teams.
  const ring: Vec2[] = Array.from({ length: 8 }, (_, k) => {
    const a = (k / 8) * Math.PI * 2
    return { x: terminal.x + Math.cos(a) * 6, z: terminal.z + Math.sin(a) * 6 }
  })
  return {
    id: 'openworld_a', length: S, width: S, floorY: C.FLOOR_Y,
    zones, pads, walls: [], pillars,
    spawns: { 0: ring, 1: ring },
    hubSpawns: { 0: ring, 1: ring },
    ceiling: null,
    props: [terminal], camera: 'follow', hub: { filler: false, moveMul: C.OPENWORLD_MOVE_MUL },
  }
}

const ARENAS: Record<string, () => Arena> = { corridor_a: corridorA, corridor_xl: corridorXL, field_40x36: field40x36, field_30x43: field30x43, field_60x26: field60x26, openworld_a: openworldA }
export const ARENA_IDS: readonly string[] = Object.keys(ARENAS)
/** D15 (2026-09-14): the arena players land on by default — hub, and therefore every match/run launched from it.
 *  `corridor_a` stays the reference geometry for tests, the bench baseline and the e2e harness. */
export const DEFAULT_HUB_ARENA_ID = 'field_60x26'

export function loadArena(id = 'corridor_a'): Arena {
  const f = ARENAS[id]
  if (!f) throw new Error(`unknown arena ${id}`)
  return f()
}

/** Team-local → world. `toWorld_B(x', z') = (−x', L − z')`, A = identity. Always pass `arena.length` (no corridor_a default). */
export function toWorld(p: Vec2, team: Team, arenaLength: number): Vec2 {
  return team === 0 ? { x: p.x, z: p.z } : { x: -p.x, z: arenaLength - p.z }
}
/** World → team-local (its own inverse). */
export const toLocal = toWorld

/** Direction vector transform (no translation). */
export function dirToWorld(d: Vec2, team: Team): Vec2 {
  return team === 0 ? { x: d.x, z: d.z } : { x: -d.x, z: -d.z }
}

export function zoneOf(arena: Arena, team: Team): Zone {
  return arena.zones.find((z) => z.team === team)!
}

export function fullBounds(arena: Arena, team: Team): Bounds {
  const z = zoneOf(arena, team)
  return { xMin: z.xMin, xMax: z.xMax, zMin: z.zMin, zMax: z.zMax }
}

/** The void between the two team pads, from their inner edges (02 §2). Arenas without two team pads
 *  (the open-world deck) report an empty gap at mid-length. */
export function gapOf(arena: Arena): { start: number; end: number } {
  const a = arena.pads.find((p) => p.team === 0), b = arena.pads.find((p) => p.team === 1)
  if (!a || !b) return { start: arena.length / 2, end: arena.length / 2 }
  return { start: a.z + a.d, end: b.z - b.d }
}

/** Is an xz point over any pad (02 §2: anything else is void)? */
export function isOnPad(arena: Arena, x: number, z: number): boolean {
  return arena.pads.some((p) =>
    p.shape === 'rect'
      ? Math.abs(x - p.x) <= p.w && Math.abs(z - p.z) <= p.d
      : Math.hypot(x - p.x, z - p.z) <= p.r,
  )
}

export function zoneTeamAt(arena: Arena, z: number): Team | null {
  for (const zn of arena.zones) if (z >= zn.zMin && z <= zn.zMax) return zn.team
  return null
}

/** Spawn for a match slot (02 §6): solo → the midpoint of the team's pair slots (x 0 on the corridors,
 *  = SPAWN_X_SOLO), pairs → the slot table. Reads the arena, never 01 §B. */
export function matchSpawn(arena: Arena, team: Team, slot: number, teamSize: number): Vec2 {
  const table = arena.spawns[team]
  if (teamSize <= 1) {
    const a = table[0], b = table[Math.min(1, table.length - 1)]
    return { x: (a.x + b.x) / 2, z: (a.z + b.z) / 2 }
  }
  return table[Math.min(slot, table.length - 1)]
}

/** Hub spawn (10 §1): slot with the fewest players within 2 m; ties → lowest index. */
export function hubSpawn(arena: Arena, team: Team, players: readonly PlayerState[]): Vec2 {
  const table = arena.hubSpawns[team]
  let best = table[0]
  let bestCount = Infinity
  for (const s of table) {
    let n = 0
    for (const p of players) if (p.alive && Math.hypot(p.pos.x - s.x, p.pos.z - s.z) < 2) n++
    if (n < bestCount) { bestCount = n; best = s }
  }
  return best
}

/** Spec §4: within TERMINAL_USE_RADIUS of any terminal prop. Arenas without terminals: anywhere counts. */
export function nearTerminal(arena: Arena, x: number, z: number): boolean {
  const ts = arena.props.filter((p) => p.kind === 'terminal')
  return ts.length === 0 || ts.some((t) => Math.hypot(x - t.x, z - t.z) <= C.TERMINAL_USE_RADIUS)
}

/** Facing the gap: +z for A, −z for B. */
export const forwardAim = (team: Team): Vec2 => (team === 0 ? { x: 0, z: 1 } : { x: 0, z: -1 })
